// src/theme/macOS.ts
import { theme } from 'antd';

export const macOSTheme = {
  // 使用浅色算法
  algorithm: theme.defaultAlgorithm,

  // 全局 Design Token
  token: {
    // 主色 - macOS 系统蓝
    colorPrimary: '#007AFF',
    colorPrimaryHover: '#0066D9',
    colorPrimaryActive: '#0055B3',
    colorPrimaryBg: '#E5F0FF',

    // 功能色（macOS 系统配色）
    colorSuccess: '#34C759',
    colorWarning: '#FF9500',
    colorError: '#FF3B30',
    colorInfo: '#007AFF',

    // 背景色系（浅灰与纯白）
    colorBgBase: '#F5F5F7',
    colorBgContainer: '#FFFFFF',
    colorBgElevated: '#FFFFFF',
    colorBgLayout: '#F5F5F7',
    colorBgSpotlight: '#1D1D1F',

    // 文字色（深灰层次）
    colorTextBase: '#1D1D1F',
    colorTextSecondary: '#6E6E73',
    colorTextTertiary: '#AEAEB2',
    colorTextQuaternary: '#C6C6C8',

    // 边框（柔和浅灰）
    colorBorder: '#D2D2D7',
    colorBorderSecondary: '#E5E5EA',

    // 大圆角（macOS 风格）
    borderRadius: 12,
    borderRadiusSM: 8,
    borderRadiusLG: 16,
    borderRadiusXS: 4,
    borderRadiusOuter: 12,

    // 系统字体
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontFamilyCode: "'SF Mono', Menlo, Monaco, Consolas, monospace",

    // 柔和阴影
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
    boxShadowSecondary: '0 8px 24px rgba(0, 0, 0, 0.12)',

    // 控制组件尺寸
    controlHeight: 32,
    controlHeightSM: 28,
    controlHeightLG: 40,
    controlHeightXS: 24,

    // 间距
    padding: 16,
    paddingSM: 12,
    paddingLG: 20,
    paddingXS: 8,
    margin: 16,
    marginSM: 12,
    marginLG: 20,
    marginXS: 8,

    // 线宽
    lineWidth: 1,
    lineWidthBold: 2,

    // 运动动画（保留适量动效）
    motion: true,
    motionUnit: 0.1,
    motionBase: 0,
  },

  // 组件级定制
  components: {
    // ========== 通用组件 ==========

    // 按钮 – 圆角、柔和光效
    Button: {
      borderRadius: 8,
      primaryShadow: '0 2px 8px rgba(0, 122, 255, 0.3)',
      defaultHoverBorderColor: '#007AFF',
      defaultHoverColor: '#007AFF',
      defaultHoverBg: 'rgba(0, 122, 255, 0.04)',
      contentFontSize: 14,
      contentFontSizeLG: 16,
      contentFontSizeSM: 12,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 28,
    },

    // ========== 布局组件 ==========

    // 栅格
    Grid: {},

    // 布局
    Layout: {
      headerBg: '#FFFFFF',
      headerColor: '#1D1D1F',
      headerPadding: '0 24px',
      bodyBg: '#F5F5F7',
      footerBg: '#F5F5F7',
      footerPadding: '16px 24px',
      triggerBg: '#FFFFFF',
      triggerColor: '#6E6E73',
      triggerHeight: 48,
      zeroTriggerHeight: 48,
    },

    // 空间
    Space: {},

    // 分割线
    Divider: {
      colorSplit: '#E5E5EA',
    },

    // 弹性布局
    Flex: {},

    // ========== 导航组件 ==========

    // 菜单 – 干净无背景，选中项淡蓝高亮
    Menu: {
      itemBg: 'transparent',
      subMenuItemBg: 'rgba(0, 0, 0, 0.02)',
      itemColor: '#1D1D1F',
      itemSelectedColor: '#007AFF',
      itemSelectedBg: 'rgba(0, 122, 255, 0.08)',
      itemActiveBg: 'rgba(0, 122, 255, 0.04)',
      itemBorderRadius: 8,
      controlHeightLG: 40,
      itemMarginInline: 4,
    },

    // 面包屑
    Breadcrumb: {
      colorText: '#6E6E73',
      colorTextSecondary: '#1D1D1F',
      lastItemColor: '#1D1D1F',
      separatorColor: '#AEAEB2',
    },

    // 分页
    Pagination: {
      borderRadius: 8,
      colorPrimary: '#007AFF',
      colorBgContainer: '#FFFFFF',
      colorBorder: '#D2D2D7',
    },

    // 步骤条
    Steps: {
      colorPrimary: '#007AFF',
      colorText: '#6E6E73',
      colorTextSecondary: '#AEAEB2',
      colorBgContainer: '#FFFFFF',
      borderRadius: 12,
    },

    // 标签页
    Tabs: {
      colorPrimary: '#007AFF',
      colorText: '#6E6E73',
      colorTextActive: '#1D1D1F',
      colorTextHover: '#007AFF',
      colorBorder: '#E5E5EA',
      cardBg: '#FFFFFF',
      cardGutter: 2,
      horizontalMargin: '0 0 16px 0',
      horizontalItemGutter: 24,
    },

    // 侧边栏
    Sider: {},

    // ========== 数据录入组件 ==========

    // 输入框 – 白底，圆角
    Input: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#D2D2D7',
      colorPrimaryHover: '#007AFF',
      borderRadius: 8,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 28,
      activeBg: '#FFFFFF',
      activeBorderColor: '#007AFF',
      hoverBorderColor: '#007AFF',
    },

    // 文本域（Input.TextArea 使用 Input 的 token）

    // 选择器
    Select: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#D2D2D7',
      borderRadius: 8,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 28,
      optionSelectedBg: 'rgba(0, 122, 255, 0.08)',
      optionActiveBg: 'rgba(0, 122, 255, 0.04)',
    },

    // 级联选择
    Cascader: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#D2D2D7',
      borderRadius: 8,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 28,
    },

    // 日期选择器
    DatePicker: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#D2D2D7',
      borderRadius: 8,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 28,
      cellHeight: 28,
      cellWidth: 36,
    },

    // 时间选择器
    TimePicker: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#D2D2D7',
      borderRadius: 8,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 28,
    },

    // 数字输入框
    InputNumber: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#D2D2D7',
      borderRadius: 8,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 28,
    },

    // 多选框
    Checkbox: {
      colorPrimary: '#007AFF',
      colorBorder: '#D2D2D7',
      borderRadius: 6,
      controlInteractiveSize: 16,
    },

    // 单选框
    Radio: {
      colorPrimary: '#007AFF',
      colorBorder: '#D2D2D7',
      radioSize: 16,
      dotSize: 8,
    },

    // 开关
    Switch: {
      colorPrimary: '#34C759',
      colorPrimaryHover: '#30B350',
      handleBg: '#FFFFFF',
      handleShadow: '0 2px 4px rgba(0,0,0,0.2)',
      trackHeight: 24,
      trackMinWidth: 44,
      innerMinMargin: 4,
    },

    // 滑块
    Slider: {
      colorPrimary: '#007AFF',
      colorPrimaryHover: '#0066D9',
      colorPrimaryBorder: '#007AFF',
      colorPrimaryBorderHover: '#0066D9',
      trackBg: '#007AFF',
      trackHoverBg: '#0066D9',
      handleColor: '#007AFF',
      handleSize: 16,
      handleSizeHover: 18,
      dotSize: 6,
    },

    // 速率评分
    Rate: {
      colorPrimary: '#FF9500',
      starSize: 20,
      starBg: '#E5E5EA',
    },

    // 上传
    Upload: {
      colorPrimary: '#007AFF',
      colorBorder: '#D2D2D7',
      borderRadius: 8,
    },

    // 表单
    Form: {
      labelColor: '#1D1D1F',
      labelFontSize: 14,
      labelHeight: 32,
      itemMarginBottom: 16,
    },

    // 提及
    Mentions: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#D2D2D7',
      borderRadius: 8,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 28,
    },

    // 自动完成
    AutoComplete: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#D2D2D7',
      borderRadius: 8,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 28,
    },

    // 树选择
    TreeSelect: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#D2D2D7',
      borderRadius: 8,
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 28,
    },

    // 颜色选择器
    ColorPicker: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#D2D2D7',
      borderRadius: 8,
      controlHeight: 32,
    },

    // 传输框
    Transfer: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#D2D2D7',
      borderRadius: 8,
      listWidth: 200,
      listHeight: 240,
    },

    // ========== 数据展示组件 ==========

    // 表格 – 浅灰表头，悬停淡蓝
    Table: {
      borderColor: '#E5E5EA',
      headerBg: '#F5F5F7',
      headerColor: '#1D1D1F',
      headerSortActiveBg: '#EAEAED',
      headerSortHoverBg: '#EAEAED',
      rowHoverBg: 'rgba(0, 122, 255, 0.04)',
      rowSelectedBg: 'rgba(0, 122, 255, 0.08)',
      rowSelectedHoverBg: 'rgba(0, 122, 255, 0.12)',
      cellPaddingInline: 16,
      cellPaddingBlock: 12,
      borderRadius: 12,
    },

    // 卡片 – 纯白背景，大圆角，轻微阴影
    Card: {
      colorBgContainer: '#FFFFFF',
      colorBorderSecondary: '#E5E5EA',
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
      borderRadiusLG: 16,
      padding: 16,
      paddingSM: 12,
      paddingLG: 20,
      headerBg: 'transparent',
      headerFontSize: 16,
    },

    // 列表
    List: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      colorText: '#1D1D1F',
      colorTextSecondary: '#6E6E73',
      padding: 16,
      paddingSM: 12,
      paddingLG: 20,
      metaMargin: 16,
    },

    // 描述列表
    Descriptions: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      colorText: '#1D1D1F',
      colorTextSecondary: '#6E6E73',
      labelColor: '#6E6E73',
      titleColor: '#1D1D1F',
      padding: 16,
      paddingSM: 12,
      paddingLG: 20,
      borderRadius: 12,
    },

    // 头像
    Avatar: {
      colorBgContainer: '#D2D2D7',
      colorText: '#FFFFFF',
      borderRadius: 12,
      borderRadiusLG: 16,
      borderRadiusSM: 8,
      size: 40,
      sizeLG: 56,
      sizeSM: 28,
    },

    // 徽标
    Badge: {
      borderRadius: 12,
      colorBgContainer: '#FF3B30',
      colorText: '#525252',
      dotSize: 8,
      shadowSize: 0,
    },

    // 标签
    Tag: {
      colorBgContainer: '#F5F5F7',
      colorBorder: '#E5E5EA',
      colorText: '#1D1D1F',
      borderRadius: 6,
      defaultBg: '#F5F5F7',
      defaultColor: '#1D1D1F',
    },

    // 气泡卡片
    Popover: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      borderRadius: 12,
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
      padding: 12,
    },

    // 文字提示
    Tooltip: {
      colorBgSpotlight: '#1D1D1F',
      colorTextLightSolid: '#FFFFFF',
      borderRadius: 6,
      fontSize: 12,
      padding: 8,
    },

    // 弹出层
    Popconfirm: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      borderRadius: 12,
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    },

    // 下拉菜单
    Dropdown: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      borderRadius: 12,
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
      padding: 4,
    },

    // 树
    Tree: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      colorText: '#1D1D1F',
      colorTextSecondary: '#6E6E73',
      nodeHoverColor: '#1D1D1F',
      nodeSelectedColor: '#1D1D1F',
      colorBgTextHover:'#1D1D1F',
      indentSize: 16,
      borderRadius: 8,
    },

    // 日历
    Calendar: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      colorText: '#1D1D1F',
      colorTextSecondary: '#6E6E73',
      fullBg: '#ffffff',
      fullPanelBg: '#FFFFFF',
      itemActiveBg: 'rgba(0, 122, 255, 0.08)',
    },

    // 统计数值
    Statistic: {
      colorBgContainer: '#FFFFFF',
      colorText: '#1D1D1F',
      colorTextSecondary: '#6E6E73',
      titleFontSize: 14,
      contentFontSize: 24,
    },

    // 评论
    Comment: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      colorText: '#1D1D1F',
      colorTextSecondary: '#6E6E73',
      padding: 16,
    },

    // 轮播图
    Carousel: {
      colorBgContainer: '#D2D2D7',
      colorText: '#FFFFFF',
      dotHeight: 4,
      dotWidth: 20,
    },

    // 折叠面板
    Collapse: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      colorText: '#1D1D1F',
      colorTextSecondary: '#6E6E73',
      headerBg: '#F5F5F7',
      borderRadius: 12,
      padding: 12,
    },

    // 时间线
    Timeline: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      colorText: '#1D1D1F',
      colorTextSecondary: '#6E6E73',
      dotBg: '#007AFF',
      tailColor: '#E5E5EA',
    },

    // ========== 反馈组件 ==========

    // 提示框
    Alert: {
      borderRadius: 8,
      colorText: '#1D1D1F',
      colorTextSecondary: '#6E6E73',
      padding: 12,
      paddingSM: 8,
    },

    // 通知
    Notification: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      borderRadius: 12,
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
      padding: 16,
      paddingLG: 20,
    },

    // 消息
    Message: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      borderRadius: 12,
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
      padding: 12,
    },

    // 模态框 – 大圆角，强阴影
    Modal: {
      borderRadiusLG: 16,
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      headerBg: 'transparent',
      titleColor: '#1D1D1F',
      titleFontSize: 18,
      padding: 24,
      paddingLG: 28,
      paddingMD: 20,
      paddingXS: 16,
    },

    // 抽屉
    Drawer: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      borderRadiusLG: 0,
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
      padding: 24,
      titleColor: '#1D1D1F',
      titleFontSize: 18,
    },

    // 进度条
    Progress: {
      colorBgContainer: '#E5E5EA',
      colorText: '#6E6E73',
      colorTextSecondary: '#AEAEB2',
      defaultColor: '#007AFF',
      remainingColor: '#E5E5EA',
      borderRadius: 12,
      lineWidth: 8,
    },

    // 结果页
    Result: {
      colorBgContainer: '#FFFFFF',
      colorText: '#1D1D1F',
      colorTextSecondary: '#6E6E73',
      titleFontSize: 24,
      subtitleFontSize: 16,
      iconSize: 48,
    },

    // 骨架屏
    Skeleton: {
      colorBgContainer: '#E5E5EA',
      colorText: '#F5F5F7',
      borderRadius: 8,
    },

    // 空状态
    Empty: {
      colorBgContainer: 'transparent',
      colorText: '#6E6E73',
      colorTextSecondary: '#AEAEB2',
    },

    // 旋转加载
    Spin: {
      colorPrimary: '#007AFF',
      colorBgContainer: 'rgba(255,255,255,0.8)',
    },

    // ========== 其他组件 ==========

    // 锚点
    Anchor: {
      colorBgContainer: 'transparent',
      colorBorder: '#E5E5EA',
      colorText: '#6E6E73',
      colorTextActive: '#007AFF',
      colorTextHover: '#007AFF',
    },

    // 回到顶部
    BackTop: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    },

    // 固钉
    Affix: {},

    // 水印
    Watermark: {},

    // 配置提供者（全局）
    ConfigProvider: {},

    // 应用容器
    App: {
      colorBgContainer: '#F5F5F7',
      colorText: '#1D1D1F',
    },

    // 二维码
    QRCode: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      borderRadius: 12,
    },

    // 游客引导
    Tour: {
      colorBgContainer: '#FFFFFF',
      colorBorder: '#E5E5EA',
      borderRadius: 12,
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    },
  },
};